<script setup>
import { Dialog, DialogPanel } from '@headlessui/vue'
import { ArrowRightCircleIcon } from '@heroicons/vue/20/solid/index.js'
import { Bars3Icon, XMarkIcon } from '@heroicons/vue/24/outline/index.js'
import { ref } from 'vue'

const navigation = [
  { name: 'Features', href: '/#feature' },
  { name: 'Price', href: '/#price' },
]

const mobileMenuOpen = ref(false)
</script>

<template>
  <header class="absolute inset-x-0 top-0 z-30">
    <nav
      class="mx-auto flex max-w-7xl items-center justify-between px-6 pt-10 lg:justify-normal lg:gap-x-10 lg:px-8"
      aria-label="Global"
    >
      <div class="relative flex lg:flex-1">
        <a href="/" class="group relative">
          <img class="relative z-10" width="180" src="/images/logo/logo-prelist.svg" alt="Prelist logo" />
        </a>
      </div>
      <div class="flex lg:hidden">
        <button
          type="button"
          class="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
          @click="mobileMenuOpen = true"
        >
          <span class="sr-only">Open main menu</span>
          <Bars3Icon class="h-6 w-6" aria-hidden="true" />
        </button>
      </div>
      <div class="hidden lg:flex lg:gap-x-12">
        <a
          v-for="item in navigation"
          :key="item.name"
          :href="item.href"
          class="text-sm font-semibold leading-6 text-gray-900 hover:underline"
        >
          {{ item.name }}
        </a>
      </div>
      <div class="hidden lg:flex lg:flex-1 lg:justify-end">
        <a
          href="/login"
          class="group flex items-center rounded-md bg-orange-600 px-3.5 py-2.5 font-semibold text-white shadow-sm outline outline-2 outline-offset-2 outline-orange-600 hover:bg-orange-500"
        >
          Sign in
          <ArrowRightCircleIcon class="ml-2 h-6 w-6 text-white transition duration-200 group-hover:translate-x-1" />
        </a>
      </div>
    </nav>
    <Dialog as="div" class="lg:hidden" :open="mobileMenuOpen" @close="mobileMenuOpen = false">
      <div class="fixed inset-0 z-30" />
      <DialogPanel
        class="fixed inset-y-0 right-0 z-30 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10"
      >
        <div class="flex items-center justify-between">
          <a href="/" class="group relative">
            <img class="relative z-10" width="180" src="/images/logo/logo-prelist.svg" alt="Prelist logo" />
          </a>
          <button type="button" class="-m-2.5 rounded-md p-2.5 text-gray-700" @click="mobileMenuOpen = false">
            <span class="sr-only">Close menu</span>
            <XMarkIcon class="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        <div class="mt-6 flow-root">
          <div class="-my-6 divide-y divide-gray-500/10">
            <div class="space-y-2 py-6">
              <a
                v-for="item in navigation"
                :key="item.name"
                :href="item.href"
                class="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
              >
                {{ item.name }}
              </a>
            </div>
            <div class="py-6">
              <a
                href="/login"
                class="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
              >
                Log in
              </a>
            </div>
          </div>
        </div>
      </DialogPanel>
    </Dialog>
  </header>

  <main>
    <div class="relative isolate mt-48">
      <svg
        class="absolute inset-0 -z-10 hidden h-full w-full stroke-gray-200 [mask-image:radial-gradient(64rem_64rem_at_top,white,transparent)] sm:block"
        aria-hidden="true"
      >
        <defs>
          <pattern
            id="55d3d46d-692e-45f2-becd-d8bdc9344f45"
            width="200"
            height="200"
            x="50%"
            y="0"
            patternUnits="userSpaceOnUse"
          >
            <path d="M.5 200V.5H200" fill="none"></path>
          </pattern>
        </defs>
        <svg x="50%" y="0" class="overflow-visible fill-gray-50">
          <path
            d="M-200.5 0h201v201h-201Z M599.5 0h201v201h-201Z M399.5 400h201v201h-201Z M-400.5 600h201v201h-201Z"
            stroke-width="0"
          ></path>
        </svg>
        <rect width="100%" height="100%" stroke-width="0" fill="url(#55d3d46d-692e-45f2-becd-d8bdc9344f45)"></rect>
      </svg>
      <div class="relative">
        <div
          class="absolute inset-x-0 top-1/2 -z-10 -translate-y-1/2 transform-gpu overflow-hidden opacity-30 blur-3xl"
          aria-hidden="true"
        >
          <div
            class="ml-[max(50%,38rem)] aspect-[1313/771] w-[82.0625rem] bg-gradient-to-tr from-[#FF9780] to-[#FCA589]"
            style="
              clip-path: polygon(
                74.1% 44.1%,
                100% 61.6%,
                97.5% 26.9%,
                85.5% 0.1%,
                80.7% 2%,
                72.5% 32.5%,
                60.2% 62.4%,
                52.4% 68.1%,
                47.5% 58.3%,
                45.2% 34.5%,
                27.5% 76.7%,
                0.1% 64.9%,
                17.9% 100%,
                27.6% 76.8%,
                76.1% 97.7%,
                74.1% 44.1%
              );
            "
          ></div>
        </div>
        <div
          class="absolute inset-x-0 top-0 -z-10 flex transform-gpu overflow-hidden pt-8 opacity-25 blur-3xl xl:justify-end"
          aria-hidden="true"
        >
          <div
            class="ml-[-22rem] aspect-[1313/771] w-[82.0625rem] flex-none origin-top-right rotate-[30deg] bg-gradient-to-tr from-[#FF9780] to-[#FCA589] xl:ml-0 xl:mr-[calc(50%-12rem)]"
            style="
              clip-path: polygon(
                74.1% 44.1%,
                100% 61.6%,
                97.5% 26.9%,
                85.5% 0.1%,
                80.7% 2%,
                72.5% 32.5%,
                60.2% 62.4%,
                52.4% 68.1%,
                47.5% 58.3%,
                45.2% 34.5%,
                27.5% 76.7%,
                0.1% 64.9%,
                17.9% 100%,
                27.6% 76.8%,
                76.1% 97.7%,
                74.1% 44.1%
              );
            "
          ></div>
        </div>
        <div class="mx-auto max-w-7xl px-6 lg:px-8">
          <div class="mx-auto max-w-xl sm:text-center">
            <h2 class="text-lg font-semibold leading-8 tracking-tight text-orange-600">Established September, 2024</h2>
            <p class="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Terms and conditions</p>
          </div>
        </div>

        <!-- text -->
        <div
          class="mx-auto mt-10 max-w-5xl rounded-xl bg-white p-6 leading-8 text-stone-800 shadow-lg shadow-gray-800/5 ring-1 ring-gray-900/5"
        >
          <h1 class="mb-4 text-3xl font-bold">Terms of Service</h1>
          <p class="mb-6">
            Welcome to Prelist.io (the “Site”), operated by Prelist LLC (“Prelist”, “we”, “our”, or “us”). These Terms
            of Service (“Terms”) govern your access to and use of our Site, software, and services (collectively, the
            “Service”). Please read these Terms carefully before using our Service. By accessing or using the Service,
            you agree to be bound by these Terms. This is a legally binding agreement between you and Prelist.
          </p>
          <p class="mb-8">
            Please read these Terms of Service (“Terms”) carefully. By using Prelist.io (the “Site”), Prelist’s
            software, or signing up for an account, you’re agreeing to these Terms. This is a legally binding agreement.
          </p>

          <h2 class="mt-10 text-2xl font-semibold">1. Use of Service</h2>
          <p>
            Your use of the Prelist Service is based on the license of Prelist’s Intellectual Property to you. We grant
            you a limited, non-transferable license to use Prelist’s Intellectual Property in accordance with these
            Terms for as long as you are a Member. Prelist reserves all rights in the Intellectual Property not
            expressly granted to you.
          </p>

          <h2 class="mt-10 text-2xl font-semibold">2. Account</h2>
          <div class="leading-8">
            <p>
              <strong>2.1 Eligibility</strong>
              By using Our Service, you represent that you have full power, capacity, and authority to accept these
              Terms.
            </p>
            <p>
              <strong>2.2 Age and Consent</strong>
              You affirm that you are at least eighteen (18) years of age or possess legal parental or guardian consent,
              and you agree to abide by and comply with these Terms of Service.
            </p>
            <p>
              <strong>2.3 Human Registration</strong>
              You must be a human to open an account. Accounts registered by “bots” or other automated methods are not
              permitted.
            </p>
            <p>
              <strong>2.4 Manual Account Creation</strong>
              You must personally and manually create your account without using any automated means aside from the
              auto-complete feature in your web browser. A third party may not create an account for you, and you must
              not allow any third party to use your information to create an account.
            </p>
            <p>
              <strong>2.5 Information Requirements</strong>
              You must provide your legal full name, valid email address, and any other information we request to
              complete your account-signup process.
            </p>
            <p>
              <strong>2.6 Account Security</strong>
              You are responsible for maintaining the security of your account and password. We will not be liable for
              any loss or damage from your failure to comply with this security obligation.
            </p>
            <p>
              <strong>2.7 Account Transfer Prohibition</strong>
              The purchase, sale, assignment, or transfer of any accounts is prohibited.
            </p>
            <p>
              <strong>2.8 Responsibility for Conduct</strong>
              You are responsible for your own conduct and activities on, through, or related to Our Service. If you
              create an account to use Our Service, you are responsible for all conduct or activities on, through, or by
              use of your account. You must immediately notify us of any unauthorized use of your account.
            </p>
          </div>

          <h2 class="mt-10 text-2xl font-semibold">3. Payments</h2>
          <div class="leading-8">
            <p>
              <strong>3.1 Service Plan Payments</strong>
              Monthly Service Plan payments are due for any month on the same date, or the closest date in that month,
              to the day you signed up with us and made your first monthly payment. You must terminate your Monthly
              Service Plan at least twenty-four (24) hours prior to the next monthly due date in order to avoid being
              charged for that month.
            </p>
            <p>
              <strong>3.2 Payment Source</strong>
              As long as you’re a Monthly Service Plan member or have an outstanding balance with us, you’ll provide us
              with a valid payment source and authorize us to deduct the monthly charges against that payment source.
              You’ll replace the information for any payment source that expires with information for a different valid
              payment source. Anyone using a payment source represents and warrants that they are authorized to use that
              payment source, and that any and all charges may be billed to that payment source and won’t be rejected.
              If, for some reason, we’re unable to process your payment, we’ll try to contact you by email so you may
              provide us with an alternate payment source. Payment sources include Credit Cards or any other means which
              We deem acceptable. Failure to perform payment shall constitute a material breach of this Agreement.
            </p>
            <p>
              <strong>3.3 Cancellation and Refunds</strong>
              You can cancel your membership at any time, and you will continue to have access to the Service through
              the end of your pre-paid billing cycle. We do not provide refunds or credits for any partial membership
              periods.
            </p>
            <p>
              <strong>3.4 Subscription and Pricing Changes</strong>
              We reserve the right to change our subscription plans or adjust pricing for our Service or any components
              thereof in any manner and at any time as we may determine in our sole and absolute discretion.
            </p>
          </div>

          <h2 class="mt-10 text-2xl font-semibold">4. Fees for Listing on Sales Channels</h2>
          <div class="leading-8">
            <p>
              Prelist does not cover any fees associated with listing, posting, or uploading products to sales channels
              that have such fees. For example, Etsy charges a 20¢ listing fee for each listing posted. You are
              responsible for all fees and charges imposed by the sales channels you choose to use with our Service.
            </p>
          </div>

          <h2 class="mt-10 text-2xl font-semibold">5. Receipts</h2>
          <div class="leading-8">
            <p>
              <strong>5.1 Receipt Issuance</strong>
              Upon completing a purchase or subscription to any of our Services, you will receive a receipt via the
              email address associated with your account. This receipt will confirm the details of your payment.
            </p>
            <p>
              <strong>5.2 Receipt Accuracy</strong>
              It is your responsibility to ensure that all billing information provided is accurate and up-to-date.
              Prelist is not liable for any discrepancies or issues arising from incorrect billing information provided
              by you.
            </p>
            <p>
              <strong>5.3 No Invoice Issuance</strong>
              We do not provide formal invoices. It is your responsibility to retain the receipt for your records.
              Prelist is not liable for any issues arising from missing or misplaced receipts.
            </p>
            <p>
              <strong>5.4 Disputes</strong>
              If you believe a receipt is incorrect, you must notify us in writing within thirty (30) days of the
              invoice date. We will investigate and resolve any legitimate discrepancies promptly. Failure to notify us
              within this period will constitute acceptance of the receipt as accurate.
            </p>
            <p>
              <strong>5.5 Payment Terms</strong>
              All invoices are due upon receipt unless otherwise specified. Late payments may incur additional fees or
              result in suspension of Services until the outstanding balance is settled.
            </p>
          </div>

          <h2 class="mt-10 text-2xl font-semibold">6. Lifetime Membership</h2>
          <p>
            <strong>6.1 Lifetime Definition</strong>
            A Lifetime Membership grants you access to Prelist’s Services for the duration of the Service’s existence,
            subject to these Terms of Service.
          </p>
          <p>
            <strong>6.2 What’s Included & Excluded</strong>
          </p>
          <ul>
            <li>
              <strong>Included Services:</strong>
              Lifetime Membership includes continuous access to the core functionalities of Prelist’s Services, as
              described at the time of purchase, for as long as Prelist continues to operate. In the event Prelist
              discontinues operations, Lifetime Membership will remain valid for a minimum of one (1) year from the date
              of purchase, after which access may be terminated.
            </li>
            <li>
              <strong>Excluded Services:</strong>
              Any new Services, premium features, or additional functionalities introduced after your Lifetime
              Membership purchase are not included and may require separate fees or subscriptions.
            </li>
          </ul>
          <p>
            <strong>6.3 Upgrade Terms</strong>
          </p>
          <ul>
            <li>
              <strong>Service Upgrades:</strong>
              Prelist reserves the right to enhance or upgrade the Services. Lifetime Members will automatically receive
              such upgrades unless the upgrades involve substantial changes to the Services’ nature or functionality, in
              which case separate terms may apply.
            </li>
            <li>
              <strong>Additional Costs:</strong>
              Any upgrades that introduce new features or services not originally included in the Lifetime Membership
              may require additional payments. Lifetime Members will be notified of such changes in advance and given
              the option to accept or decline the additional services.
            </li>
            <li>
              <strong>Termination of Lifetime Membership</strong>
              Prelist reserves the right to terminate Lifetime Memberships if there is a significant breach of these
              Terms of Service or if maintaining the Lifetime Membership becomes untenable due to business reasons. In
              such cases, affected members will be notified and may receive a pro-rated refund for unused portions of
              their membership.
            </li>
          </ul>

          <h2 class="mt-10 text-2xl font-semibold">7. Communications</h2>
          <div class="leading-8">
            <p>
              <strong>7.1.</strong>
              By using Our Service, you agree to receive electronic communications from Prelist. These may include
              updates, newsletters, marketing materials, and transactional messages related to your account and our
              Services.
            </p>
            <p>
              <strong>7.2.</strong>
              You consent to the use of your email address and other contact information for these communications. You
              can opt-out of receiving certain communications by following the instructions provided in the emails or by
              contacting us directly.
            </p>
            <p>
              <strong>7.3.</strong>
              All communications from Prelist will be considered delivered when sent to the email address associated
              with your account, unless otherwise specified.
            </p>
          </div>

          <h2 class="mt-10 text-2xl font-semibold">8. Content</h2>
          <div class="leading-8">
            <p>
              <strong>8.1.</strong>
              You are solely responsible for all content you upload, post, or otherwise transmit through Our Service,
              including text, images, videos, and other materials.
            </p>
            <p>
              <strong>8.2.</strong>
              By submitting content to Prelist, you grant us a non-exclusive, worldwide, royalty-free license to use,
              reproduce, modify, and display your content in connection with providing the Service.
            </p>
            <p>
              <strong>8.3.</strong>
              We reserve the right to remove any content that violates these Terms or is deemed inappropriate, at our
              sole discretion.
            </p>
          </div>

          <h2 class="mt-10 text-2xl font-semibold">9. Prohibited Uses</h2>
          <div class="leading-8">
            <p>
              <strong>9.1.</strong>
              You may not use Our Service for any unlawful purpose or in violation of any applicable laws and
              regulations.
            </p>
            <p>
              <strong>9.2.</strong>
              Prohibited activities include, but are not limited to:
            </p>
            <ul class="ml-6 list-disc">
              <li>Attempting to gain unauthorized access to any part of the Service or its related systems.</li>
              <li>Distributing malware or other harmful software.</li>
              <li>Engaging in fraudulent activities or misrepresenting your identity.</li>
              <li>Using the Service to harass, defame, or harm others.</li>
            </ul>
            <p>
              <strong>9.3.</strong>
              We reserve the right to suspend or terminate your account if you engage in any prohibited use.
            </p>
          </div>

          <h2 class="mt-10 text-2xl font-semibold">10. Analytics</h2>
          <div class="leading-8">
            <p>
              <strong>10.1.</strong>
              We may collect and analyze data about your use of the Service to improve our offerings and user
              experience.
            </p>
            <p>
              <strong>10.2.</strong>
              This data may include information about your device, browser, usage patterns, and other technical details.
            </p>
            <p>
              <strong>10.3.</strong>
              We use this information in accordance with our Privacy Policy, which is incorporated into these Terms by
              reference.
            </p>
          </div>

          <h2 class="mt-10 text-2xl font-semibold">11. Intellectual Property</h2>
          <div class="leading-8">
            <p>
              <strong>11.1.</strong>
              All intellectual property rights in the Service and its content are owned by Prelist or our licensors.
            </p>
            <p>
              <strong>11.2.</strong>
              You may not reproduce, distribute, modify, create derivative works of, publicly display, or otherwise
              exploit any part of the Service without our prior written permission.
            </p>
            <p>
              <strong>11.3.</strong>
              Any trademarks, logos, or service marks displayed on the Site are the property of Prelist or their
              respective owners and may not be used without authorization.
            </p>
          </div>

          <h2 class="mt-10 text-2xl font-semibold">12. Member Warranties</h2>
          <div class="leading-8">
            <p>
              <strong>12.1.</strong>
              You agree not to:
            </p>
            <ul class="ml-6 list-disc">
              <li>
                Upload, post, email, or otherwise transmit any material that contains software viruses and/or any other
                computer code, files, or programs designed to interrupt, destroy, or limit the functionality of any
                computer software or hardware or telecommunications equipment.
              </li>
              <li>
                Send or transmit pornography or other sexually explicit communications, communications offering to sell
                illegal goods or services, communications that violate CAN-SPAM laws, or marketing or commercial
                communications without permission.
              </li>
              <li>
                Infringe on any third party’s ownership rights by using any material in your transmissions or uploads
                that is not owned or licensed by you. This includes text, photos, graphics, and other content.
              </li>
              <li>
                Use any misleading or incorrect names, addresses, email addresses, subject lines, photographs, content,
                or other information on the Website or in any communications or transmissions sent using Our Service.
              </li>
              <li>
                Breach or circumvent any laws, third party rights, or our systems, policies, or determinations of your
                account status.
              </li>
              <li>Use any robot, spider, scraper, or other automated means to access our Service for any purpose.</li>
              <li>Harvest or otherwise collect information about members without their consent.</li>
              <li>
                Upload or transmit harassing, offensive, obscene, defamatory, threatening, or malicious content or
                communications.
              </li>
            </ul>
          </div>
          <h2 class="mt-10 text-2xl font-semibold">13. Disclaimer of Warranties</h2>
          <div class="leading-8">
            <p>
              TO THE FULLEST EXTENT PERMITTED BY THE APPLICABLE LAW, PRELIST OFFERS THE SITE AND SERVICES AS-IS AND
              MAKES NO REPRESENTATIONS OR WARRANTIES OF ANY KIND CONCERNING THE SITE AND/OR ITS SERVICES, EXPRESS,
              IMPLIED, STATUTORY OR OTHERWISE, INCLUDING, WITHOUT LIMITATION, MERCHANTABILITY AND FITNESS FOR A
              PARTICULAR PURPOSE. PRELIST DOES NOT WARRANT THAT THE FUNCTIONS OR CONTENT CONTAINED ON THE SITE AND/OR
              SERVICES WILL BE UNINTERRUPTED OR ERROR-FREE, THAT DEFECTS WILL BE CORRECTED, OR THAT OUR SERVERS ARE FREE
              OF VIRUSES OR OTHER HARMFUL COMPONENTS.
            </p>
            <p class="mt-4">YOUR USE OF THE SERVICE IS AT YOUR SOLE RISK.</p>
          </div>

          <h2 class="mt-10 text-2xl font-semibold">14. Limitation of Liability</h2>
          <div class="leading-8">
            <p>
              EXCEPT TO THE MINIMUM EXTENT REQUIRED BY APPLICABLE LAW AND THEN ONLY TO THAT EXTENT, IN NO EVENT WILL
              PRELIST, ITS EMPLOYEES, OFFICERS, DIRECTORS, AFFILIATES, OR AGENTS (THE "PRELIST TEAM") BE LIABLE TO YOU
              ON ANY LEGAL THEORY FOR ANY INCIDENTAL, DIRECT, INDIRECT, PUNITIVE, ACTUAL, CONSEQUENTIAL, SPECIAL,
              EXEMPLARY OR OTHER DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF REVENUE OR INCOME, LOST PROFITS, PAIN
              AND SUFFERING, EMOTIONAL DISTRESS, COST OF SUBSTITUTE GOODS OR SERVICES, OR SIMILAR DAMAGES SUFFERED OR
              INCURRED BY YOU OR ANY THIRD PARTY THAT ARISE IN CONNECTION WITH THE WEBSITE OR SERVICES (OR THE
              TERMINATION THEREOF FOR ANY REASON), EVEN IF THE PRELIST TEAM HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH
              DAMAGES.
            </p>
            <p class="mt-4">
              OUR TOTAL LIABILITY FOR ALL CLAIMS MADE ABOUT THE SERVICE IN ANY MONTH WILL BE NO MORE THAN WHAT YOU PAID
              TO US THE MONTH BEFORE.
            </p>
          </div>

          <h2 class="mt-10 text-2xl font-semibold">15. Changes to Service and Terms</h2>
          <div class="leading-8">
            <p>
              <strong>15.1. Modifications to the Service</strong>
            </p>
            <p class="mt-2">
              We may modify, suspend, or discontinue the Service, or any part thereof, at any time and without notice.
              We are not liable to you or any third party for any modification, suspension, or discontinuation of the
              Service. We may also introduce new features or functionalities to enhance the Service, and you agree to
              receive such updates as part of your use of the Service.
            </p>
            <p class="mt-4">
              <strong>15.2. Changes to Terms</strong>
            </p>
            <p class="mt-2">
              We may change the terms of this Agreement or our pricing at any time by posting the changes to this Site
              or notifying you by email. Any changes will be effective immediately upon posting or as specified in the
              communication. Your continued use of the Service after any changes constitutes your acceptance of the new
              Terms.
            </p>
          </div>

          <h2 class="mt-10 text-2xl font-semibold">16. Termination</h2>
          <div class="leading-8">
            <p>
              <strong>16.1. Termination by Either Party</strong>
            </p>
            <p class="mt-2">
              We or you may terminate this Agreement at any time by giving notice to the other party. We may suspend our
              Service to you at any time, with or without cause.
            </p>
            <p class="mt-4">
              <strong>16.2. Deletion Upon Termination</strong>
            </p>
            <p class="mt-2">
              Only if we terminate your account without cause, will we refund you a pro-rated portion of any pre-payment
              plan. If your account is terminated, we may permanently delete your account and all data associated with
              it from our Site.
            </p>
            <p class="mt-4">
              <strong>16.3. Inactive Account Deletion</strong>
            </p>
            <p class="mt-2">
              If you don't log in to your account for twelve (12) or more months, we may treat your account as
              "inactive" and permanently delete the account and all data associated with it.
            </p>
          </div>

          <h2 class="mt-10 text-2xl font-semibold">17. Account & Data</h2>
          <div class="leading-8">
            <p>
              <strong>17.1. Deletion Policy</strong>
            </p>
            <ul class="ml-6 mt-2 list-disc">
              <li>
                <strong>Account Deletion by User:</strong>
                You may request the deletion of your account at any time by contacting us through the provided support
                channels. Upon receiving your request, we will delete your account and all associated data within thirty
                (30) days, unless retention is required by law.
              </li>
              <li class="mt-2">
                <strong>Account Deletion by Prelist:</strong>
                We reserve the right to delete your account and associated data if you violate these Terms of Service or
                engage in prohibited activities. In such cases, you will be notified of the deletion, and no refund will
                be provided for any remaining subscription period.
              </li>
              <li class="mt-2">
                <strong>Data Retention:</strong>
                Even after account deletion, certain information may be retained as required by law or for legitimate
                business purposes, such as fraud prevention and dispute resolution.
              </li>
            </ul>
          </div>

          <h2 class="mt-10 text-2xl font-semibold">18. Acknowledgement</h2>
          <div class="leading-8">
            <p>
              <strong>18.1. Agreement to Terms</strong>
            </p>
            <p class="mt-2">
              By using the Service, you acknowledge that you have read, understood, and agree to be bound by these
              Terms.
            </p>
            <p class="mt-4">
              <strong>18.2. Entire Agreement</strong>
            </p>
            <p class="mt-2">
              These Terms constitute the entire agreement between you and Prelist regarding your use of the Service and
              supersede any prior agreements or understandings.
            </p>
          </div>

          <h2 class="mt-10 text-2xl font-semibold">19. Privacy Policy</h2>
          <div class="leading-8">
            <p>
              Prelist may use and disclose your information according to our Privacy Policy. The terms of Our Privacy
              Policy are incorporated into these Terms of Service by reference.
            </p>
          </div>

          <h2 class="mt-10 text-2xl font-semibold">20. Limitation on User Liability</h2>
          <div class="leading-8">
            <p>
              <strong>User Liability:</strong>
              You agree to indemnify and hold harmless Prelist LLC, its affiliates, employees, and partners from any
              claims, damages, liabilities, or expenses arising from your breach of these Terms or your use of the
              Service in violation of any applicable law.
            </p>
          </div>

          <h2 class="mt-10 text-2xl font-semibold">21. Third-Party Integrations</h2>
          <div class="leading-8">
            <p>
              <strong>Third-Party Services:</strong>
              The Service may integrate with or allow you to connect to third-party platforms. You are responsible for
              complying with the terms of those third-party platforms, and Prelist LLC is not liable for any
              disruptions, errors, or disputes arising from those integrations, or associated fees.
            </p>
          </div>

          <h2 class="mt-10 text-2xl font-semibold">22. No Guarantees of Platform Uptime</h2>
          <div class="leading-8">
            <p>
              <strong>Uptime Disclaimer:</strong>
              While we strive to ensure that the Service is available at all times, we do not guarantee uninterrupted or
              error-free availability. Downtime may occur due to maintenance, technical issues, or external factors
              outside our control.
            </p>
          </div>

          <h2 class="mt-10 text-2xl font-semibold">23. Tax Responsibility</h2>
          <div class="leading-8">
            <p>
              <strong>Tax Obligations:</strong>
              You are solely responsible for determining, collecting, reporting, and remitting any taxes, duties, fees,
              or other charges associated with your use of the Service or the sales generated through third-party
              platforms.
            </p>
          </div>

          <h2 class="mt-10 text-2xl font-semibold">24. Feedback and Suggestions</h2>
          <div class="leading-8">
            <p>
              <strong>Feedback:</strong>
              Any feedback, suggestions, or ideas you provide to us regarding the Service shall be deemed
              non-confidential, and Prelist LLC will have the right to use such feedback for any purpose without
              restriction or obligation to you.
            </p>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>
